/**
 * @fileoverview SessionViewerPortal Component
 * 
 * Displays a SessionViewer using react-reverse-portal's OutPortal component.
 * This allows the viewer to be moved between different locations (grid/focus)
 * without remounting, preserving LiveKit connections and component state.
 * 
 * **How It Works:**
 * 
 * 1. **OutPortal**: Renders the portal node's content at this location
 * 2. **No Remounting**: Component stays mounted when moved between portals
 * 3. **State Preservation**: All component state, LiveKit connections persist
 * 4. **Component Control**: Dynamically enables/disables components based on focus state
 * 
 * **Why React-Reverse-Portal:**
 * 
 * - React-native solution (no DOM/React conflicts)
 * - Components never remount when moved
 * - State is preserved (selected tabs, scroll position, etc.)
 * - LiveKit connections stay alive
 * - Smooth transitions without video flickering
 * - Eliminates manual DOM manipulation and race conditions
 * 
 * **Performance Optimization:**
 * 
 * This component does NOT subscribe to the sessions object from Zustand.
 * It uses `getState()` to access session data without triggering re-renders
 * when session properties update.
 * 
 * @module vtc/contexts/SessionViewerContext/SessionViewerPortal
 */

import React, { useEffect } from 'react';
import * as portals from 'react-reverse-portal';
import { SessionViewer } from '@shared/components/proctoring/SessionViewer';
import { useSessionViewers } from './hooks';
import { getEnabledComponentsForSession } from './utils';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';

/**
 * SessionViewerPortal Component
 * 
 * Displays a SessionViewer using react-reverse-portal's OutPortal.
 * The viewer can be moved between different locations without remounting,
 * preserving LiveKit connections and component state.
 * 
 * **Usage:**
 * ```jsx
 * // In SessionCard (grid view)
 * <SessionViewerPortal sessionSid={sessionSid} isFocused={false} />
 * 
 * // In Focus view
 * <SessionViewerPortal sessionSid={focusedSessionSid} isFocused={true} />
 * ```
 * 
 * @component
 * @param {Object} props
 * @param {string} props.sessionSid - Session identifier
 * @param {boolean} [props.isFocused=false] - Whether this viewer is in focus mode
 * @returns {JSX.Element}
 * 
 * @example
 * // Display a session in grid view (controls disabled)
 * <SessionViewerPortal 
 *   sessionSid="session_123" 
 *   isFocused={false} 
 * />
 * 
 * @example
 * // Display a session in focus view (controls enabled)
 * <SessionViewerPortal 
 *   sessionSid="session_123" 
 *   isFocused={true} 
 * />
 */
export function SessionViewerPortal({ sessionSid, isFocused = false }) {
    const { getOrCreatePortalNode } = useSessionViewers();

    // Get session data to determine if this is a checkout session
    const session = useSessionStore((state) => state.sessions[sessionSid]);
    const audioDevices = useAppStore((state) => state.audioDevices);

    // Check if this is a checkout session (not in pool)
    const isCheckoutSession = session?.location === 'checkout';

    // Effect: Enable/disable components based on focus state
    useEffect(() => {
        if (!sessionSid) return;

        // Use getState() to avoid subscribing to sessions
        const sessions = useSessionStore.getState().sessions;
        const viewerRef = sessions[sessionSid]?.viewerRef;
        if (!viewerRef?.current) return;

        // NOTE: Disabled because SessionViewerPool already passes enabledComponents as props
        // and Session.jsx handles the updates. This was causing redundant updateEnabledComponents
        // calls that resulted in race conditions and multiple re-renders.
        // The enabledComponents are now managed solely through props from SessionViewerPool.

        // if (isFocused) {
        //     // Enable all controls when in focus mode
        //     viewerRef.current.enableComponents(getEnabledComponentsForSession(sessionSid));
        // } else {
        //     // Disable controls when in grid mode
        //     viewerRef.current.disableComponents([
        //         'controls',
        //         'sessionDetails',
        //         'chat',
        //         'microphone',
        //         'settings',
        //         'timeline',
        //     ]);
        // }
    }, [isFocused, sessionSid]);

    // For checkout sessions, render SessionViewer directly
    if (isCheckoutSession && session) {
        const viewerProps = session.viewerProps;
        const viewerRef = session.viewerRef;

        if (!viewerProps) {
            return (
                <div key="loading" style={{ padding: '20px', textAlign: 'center' }}>
                    Loading session...
                </div>
            );
        }

        // Get session-specific configuration
        const sessionConfig = getEnabledComponentsForSession(sessionSid);
        const { video: sessionVideo, ...sessionConfigWithoutVideo } = sessionConfig;

        const mergedEnabledComponents = {
            ...viewerProps.enabledComponents,
            ...sessionConfigWithoutVideo
        };

        const videoConfig = sessionVideo || viewerProps.video;

        return (
            <div
                key="checkout-portal"
                data-session-portal={sessionSid}
                data-is-checkout-session="true"
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <SessionViewer
                    ref={viewerRef}
                    {...viewerProps}
                    //enabledComponents={mergedEnabledComponents}
                    video={videoConfig}
                    audioInputDeviceId={audioDevices?.inputDeviceId}
                    audioOutputDeviceId={audioDevices?.outputDeviceId}
                />
            </div>
        );
    }

    // For testing/review sessions, use OutPortal to display the session
    // Get the portal node for this session
    const portalNode = getOrCreatePortalNode(sessionSid);

    return (
        <div
            key="pool-portal"
            data-session-portal={sessionSid}
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            {/* OutPortal displays the SessionViewer without remounting */}
            <portals.OutPortal node={portalNode} />
        </div>
    );
}
